// Administration › Conservation des données (RGPD art. 5.1.e) : durées réglées par la société (jamais sous leur plancher),
// prochaine échéance, dernier passage, SIMULATION (ce qui serait effacé aujourd'hui, sans aucun effet), passage manuel, chantiers
// terminés sans date de réception (rien de leur historique ne s'efface tant qu'elle n'est pas saisie).
// Tout est appliqué par le SERVEUR (passage quotidien automatique) : cet écran règle, montre et simule.
import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, FlaskConical, Play, Search, Settings2 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { date, dateHeure, pluriel } from '../../lib/format.ts';
import { Alerte, Badge, Bouton, ChampChoix, Chargement, Dialogue, EtatErreur, Panneau, useConfirmation, useNotifier } from '../../components/ui.tsx';
import { libelleDuree, type CleDuree } from '@shared/conservation.ts';

interface Duree { cle: CleDuree; libelle: string; unite: 'ans' | 'mois'; defaut: number; min: number; max: number; depart: string; effet: string;
  fondement: string; valeur: number; libelle_valeur: string }
interface Echeance { categorie: string; libelle: string; date: string; chantier?: { id: string; reference: string | null; titre: string } }
interface Passage { id: string; mode: string; statut: string; comptes: Record<string, number>; erreur: string | null; le: string; fini_le: string | null }
interface EtatConservation {
  durees: Duree[];
  engagements: { fin_contrat: string | null; ans: number; fondement: string; echeance: string | null };
  reglages_par_defaut: boolean; modifie_le: string | null; aujourdhui: string;
  prochaine_echeance: Echeance | null; echeances: Echeance[]; nb_echeances: number;
  dernier_passage: Passage | null; passage_du_jour: boolean; passages: Passage[];
  sans_reception: { id: string; reference: string | null; titre: string; statut: string; demo: boolean; site: string | null; dernier_jour: string | null }[];
  fichiers_en_attente: number;
  libelles_categories: Record<string, string>;
}
interface Resultat {
  simulation: boolean; total: number; comptes: Record<string, number>; lignes: { categorie: string; libelle: string; nombre: number }[];
  chantiers: { id: string; reference: string | null; titre: string; categories: string[] }[]; fichiers_restants: number;
}

const CLE = ['conservation'];
const total = (c: Record<string, number>) => Object.values(c ?? {}).reduce((s, x) => s + (Number(x) || 0), 0);
const libelleChantier = (c: { reference: string | null; titre: string }) => [c.reference, c.titre].filter(Boolean).join(' — ');
const echu = (j: string, aujourdhui: string) => j <= aujourdhui;
const traites = (n: number) => (n ? pluriel(n, 'élément traité', 'éléments traités') : 'rien à effacer');

function DialogueDurees({ e, fermer }: { e: EtatConservation; fermer: () => void }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [v, setV] = useState<Record<string, number>>(Object.fromEntries(e.durees.map((d) => [d.cle, d.valeur])));
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const enregistrer = async () => {
    setErreur(null); setEnvoi(true);
    try {
      const n = await api.put<EtatConservation>('/api/organisation/conservation', { durees: v });
      qc.setQueryData(CLE, n);
      notifier('Durées de conservation enregistrées.');
      fermer();
    } catch (x) { setErreur(messageErreur(x)); } finally { setEnvoi(false); }
  };
  const options = (d: Duree) => {
    const pas = d.unite === 'mois' && d.max - d.min > 12 ? 6 : 1;
    const l: number[] = [];
    for (let n = d.min; n <= d.max; n += pas) l.push(n);
    if (!l.includes(d.max)) l.push(d.max);
    if (!l.includes(v[d.cle])) l.push(v[d.cle]);
    return l.sort((a, b) => a - b).map((n) => ({ valeur: String(n), libelle: `${libelleDuree(d.cle, n)}${n === d.defaut ? ' (par défaut)' : ''}` }));
  };
  return (
    <Dialogue titre="Durées de conservation" ouvert fermer={fermer} large
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} onClick={enregistrer}>Enregistrer</Bouton></>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        {e.durees.map((d) => (
          <ChampChoix key={d.cle} libelle={d.libelle} value={String(v[d.cle])} onChange={(x) => setV({ ...v, [d.cle]: Number(x.target.value) })}
            options={options(d)} aide={`À partir de ${d.depart}. Jamais moins de ${libelleDuree(d.cle, d.min)} : ${d.fondement}`} />
        ))}
        <p className="petit discret" style={{ margin: 0 }}>Fin du contrat avec l’éditeur : {e.engagements.fin_contrat ? date(e.engagements.fin_contrat) : 'non renseignée'}.
          Posée par l’éditeur de TRAME seulement (les engagements du représentant légal sont sa preuve) ; elle ne se règle pas ici.</p>
      </div>
    </Dialogue>
  );
}

function ResultatPurge({ r, fermer }: { r: Resultat; fermer: () => void }) {
  return (
    <Panneau titre={r.simulation ? 'Simulation : ce qui serait effacé aujourd’hui' : 'Passage effectué'}
      actions={<Bouton compact variante="fantome" onClick={fermer}>Fermer</Bouton>}>
      <div className="pile">
        {r.total === 0
          ? <Alerte ton="succes" titre="Rien à effacer aujourd’hui">Aucune donnée n’a atteint sa durée de conservation.{r.simulation ? ' Rien n’a été modifié.' : ''}</Alerte>
          : <Alerte ton={r.simulation ? 'ambre' : 'info'} titre={r.simulation ? `${pluriel(r.total, 'élément')} ${r.total > 1 ? 'partiraient' : 'partirait'} au prochain passage` : pluriel(r.total, 'élément traité', 'éléments traités')}>
            {r.simulation ? 'Simulation seulement : rien n’a été effacé.' : 'Seuls des nombres sont gardés dans le journal des passages, jamais le contenu.'}</Alerte>}
        {r.lignes.length > 0 && (
          <dl className="fiche cons-comptes">
            {r.lignes.map((l) => <div key={l.categorie}><dt>{l.libelle}</dt><dd className="chiffre">{l.nombre}</dd></div>)}
          </dl>
        )}
        {r.chantiers.length > 0 && (
          <div className="pile" style={{ gap: 'var(--e-1)' }}>
            <strong className="petit">Chantiers concernés</strong>
            <ul className="cons-liste">
              {r.chantiers.map((c) => (
                <li key={c.id}><Link to={`/interventions/chantiers/${c.id}`}>{libelleChantier(c)}</Link>
                  <span className="petit discret"> — {c.categories.map((x) => (x === 'temps' ? 'temps agrégé sans nom' : 'historique anonymisé, photos effacées')).join(' ; ')}</span></li>
              ))}
            </ul>
          </div>
        )}
        {r.fichiers_restants > 0 && <Alerte ton="ambre">{pluriel(r.fichiers_restants, 'fichier')} n’a pas pu être effacé du stockage : nouvel essai au prochain passage.</Alerte>}
      </div>
    </Panneau>
  );
}

export function OngletConservation() {
  const q = useQuery({ queryKey: CLE, queryFn: () => api.get<EtatConservation>('/api/organisation/conservation') });
  const qc = useQueryClient();
  const notifier = useNotifier();
  const { confirmer, element } = useConfirmation();
  const [dialogue, setDialogue] = useState(false);
  const [resultat, setResultat] = useState<Resultat | null>(null);
  const [envoi, setEnvoi] = useState<null | 'simulation' | 'passage'>(null);
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const e = q.data!;
  const simuler = async () => {
    setEnvoi('simulation');
    try { setResultat(await api.post<Resultat>('/api/organisation/conservation/simulation')); }
    catch (x) { notifier(messageErreur(x), true); } finally { setEnvoi(null); }
  };
  const lancer = async () => {
    if (!(await confirmer('Lancer le passage maintenant ?', 'Les données dont la durée de conservation est écoulée sont effacées ou anonymisées, comme au passage automatique de chaque jour. C’est définitif. Faites d’abord une simulation pour voir ce qui partira.', 'Lancer le passage'))) return;
    setEnvoi('passage');
    try {
      const r = await api.post<Resultat & { etat: EtatConservation }>('/api/organisation/conservation/passage');
      setResultat(r);
      qc.setQueryData(CLE, r.etat);
      notifier(`Passage effectué : ${traites(r.total)}.`);
    } catch (x) { notifier(messageErreur(x), true); } finally { setEnvoi(null); }
  };
  const p = e.prochaine_echeance;
  const dp = e.dernier_passage;
  return (
    <div className="pile">
      <Panneau titre="Conservation des données">
        <div className="pile">
          <Alerte ton={p && echu(p.date, e.aujourdhui) ? 'ambre' : 'info'}
            titre={p ? `Prochaine échéance : ${echu(p.date, e.aujourdhui) ? 'au prochain passage' : `le ${date(p.date)}`}` : 'Aucune échéance prévue'}>
            {p ? <>{p.libelle}{p.chantier ? ` — ${libelleChantier(p.chantier)}` : ''}.</> : 'Rien ne sera effacé tant qu’aucune donnée n’atteint sa durée de conservation.'}
            {' '}{dp ? `Dernier passage le ${dateHeure(dp.le)} (${dp.mode}) : ${dp.statut === 'echec' ? 'en échec, rien n’a été effacé' : traites(total(dp.comptes))}.`
              : 'Aucun passage pour l’instant : le premier a lieu au démarrage du serveur, puis chaque jour.'}
          </Alerte>
          <div className="barre-actions">
            <Bouton variante="principal" icone={<Search aria-hidden />} chargement={envoi === 'simulation'} onClick={simuler}>Simuler la purge</Bouton>
            <Bouton icone={<Settings2 aria-hidden />} onClick={() => setDialogue(true)}>Régler les durées</Bouton>
            <Bouton variante="fantome" icone={<Play aria-hidden />} chargement={envoi === 'passage'} onClick={lancer}>Lancer le passage maintenant</Bouton>
          </div>
          <p className="petit discret" style={{ margin: 0 }}>Passage automatique chaque jour{e.passage_du_jour ? ' (fait aujourd’hui)' : ''}, par entreprise ; la démonstration
            n’est jamais purgée. {e.reglages_par_defaut ? 'Durées par défaut de TRAME.' : `Durées modifiées le ${e.modifie_le ? dateHeure(e.modifie_le) : '…'}.`}</p>
        </div>
      </Panneau>
      {resultat && <ResultatPurge r={resultat} fermer={() => setResultat(null)} />}
      <Panneau serre titre="Durées de conservation">
        <ul className="cons-durees">
          {e.durees.map((d) => (
            <li key={d.cle}>
              <div className="cons-duree-tete"><strong>{d.libelle}</strong><Badge ton="accent" icone={<CalendarClock size={14} aria-hidden />}>{d.libelle_valeur}</Badge></div>
              <span className="petit">À partir de {d.depart}. {d.effet}</span>
              <details className="petit discret"><summary>Pourquoi au moins {libelleDuree(d.cle, d.min)} ?</summary>{d.fondement}</details>
            </li>
          ))}
          <li>
            <div className="cons-duree-tete"><strong>Engagements du représentant légal</strong>
              <Badge ton="neutre" icone={false}>{`${e.engagements.ans} ans après la fin du contrat`}</Badge></div>
            <span className="petit">{e.engagements.fin_contrat ? `Fin du contrat : ${date(e.engagements.fin_contrat)} — effacement le ${date(e.engagements.echeance)}.`
              : 'Fin du contrat non renseignée : rien n’est effacé.'} Date posée par l’éditeur de TRAME seulement (preuve de l’engagement) ; durée non réglable.</span>
          </li>
        </ul>
      </Panneau>
      <Panneau serre titre={`Chantiers terminés sans date de réception (${e.sans_reception.length})`}>
        {e.sans_reception.length === 0 ? <p className="petit secondaire" style={{ margin: 'var(--e-4) var(--e-5)' }}>Aucun : chaque chantier terminé a sa date de réception.</p> : (
          <div className="pile" style={{ padding: 'var(--e-3) var(--e-5) var(--e-4)', gap: 'var(--e-2)' }}>
            <p className="petit secondaire" style={{ margin: 0 }}>Tant que la réception n’est pas saisie, l’historique et les photos de ces chantiers ne sont jamais effacés.
              Saisissez-la depuis la page du chantier (chargé d’affaires, référent technique ou administrateur).</p>
            <ul className="cons-liste">
              {e.sans_reception.map((c) => (
                <li key={c.id}><Link to={`/interventions/chantiers/${c.id}`}>{libelleChantier(c)}</Link>
                  <span className="petit discret">{c.site ? ` — ${c.site}` : ''}{c.dernier_jour ? ` — dernière activité le ${date(c.dernier_jour)}` : ''}</span>
                  {c.demo && <> <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge></>}</li>
              ))}
            </ul>
          </div>
        )}
      </Panneau>
      <Panneau serre titre={`Prochaines échéances (${e.nb_echeances})`}>
        {e.echeances.length === 0 ? <p className="petit secondaire" style={{ margin: 'var(--e-4) var(--e-5)' }}>Aucune pour l’instant.</p> : (
          <ul className="cons-liste" style={{ padding: 'var(--e-3) var(--e-5) var(--e-4)' }}>
            {e.echeances.map((x, i) => (
              <li key={`${x.categorie}-${x.chantier?.id ?? i}`}><span className="chiffre">{echu(x.date, e.aujourdhui) ? 'Prochain passage' : date(x.date)}</span> — {x.libelle}
                {x.chantier && <> : <Link to={`/interventions/chantiers/${x.chantier.id}`}>{libelleChantier(x.chantier)}</Link></>}</li>
            ))}
          </ul>
        )}
      </Panneau>
      <Panneau serre titre="Derniers passages">
        {e.passages.length === 0 ? <p className="petit secondaire" style={{ margin: 'var(--e-4) var(--e-5)' }}>Aucun passage pour l’instant.</p> : (
          <ul className="cons-liste" style={{ padding: 'var(--e-3) var(--e-5) var(--e-4)' }}>
            {e.passages.map((x) => (
              <li key={x.id}><span className="chiffre">{dateHeure(x.le)}</span> — {x.mode === 'manuel' ? 'manuel' : 'automatique'} —{' '}
                {x.statut === 'echec' ? <Badge ton="danger">En échec, rien effacé</Badge>
                  : total(x.comptes) ? Object.entries(x.comptes).filter(([, n]) => n > 0).map(([k, n]) => `${e.libelles_categories[k] ?? k} : ${n}`).join(' ; ')
                  : 'rien à effacer'}</li>
            ))}
          </ul>
        )}
        {e.fichiers_en_attente > 0 && <Alerte ton="ambre">{pluriel(e.fichiers_en_attente, 'fichier')} en attente d’effacement du stockage (nouvel essai au prochain passage).</Alerte>}
      </Panneau>
      {dialogue && <DialogueDurees e={e} fermer={() => setDialogue(false)} />}
      {element}
    </div>
  );
}
